import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  X,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Upload,
  Video,
  Link as LinkIcon,
  Sparkles,
  Trash2,
  Truck,
  Bike,
  Car,
  Anchor,
  Loader2,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { key: "trucking", label: "Trucking", Icon: Truck },
  { key: "biking", label: "Biking", Icon: Bike },
  { key: "cars", label: "Cars", Icon: Car },
  { key: "boating", label: "Boating", Icon: Anchor },
];

const MAX_PHOTOS = 5;
const MIN_PHOTOS = 2;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const PHOTO_MAX_DIM = 1600; // px

// ---------- utils ----------
async function resizeImageToDataURL(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  let { width, height } = img;
  const scale = Math.min(1, PHOTO_MAX_DIM / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.85);
}

const STEPS = [
  { key: "about", title: "About You" },
  { key: "lane", title: "Your Lane" },
  { key: "gear", title: "Your Gear" },
  { key: "photos", title: "Your Photos" },
  { key: "video", title: "Intro Video" },
  { key: "review", title: "Review & Submit" },
];

// ---------- main component ----------
export default function ApplyModal({ open, onClose, showContext }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const isBNB = (showContext?.kind || "") === "built_not_bought";

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    age: "",
    categories: [],
    story: "",
    years_in_culture: "",
    vehicle: "",
    social_handle: "",
    photos: [], // [{data, name}]
    video_file_id: null,
    video_filename: "",
    video_url: "",
    // Built Not Bought — optional extra question
    bnb_exchange_custom_work: "",
  });

  // lock body scroll when open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // reset when closed
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(0);
        setError("");
        setSuccess(null);
        setSubmitting(false);
      }, 250);
    }
  }, [open]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleCategory = (k) =>
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(k)
        ? f.categories.filter((x) => x !== k)
        : [...f.categories, k],
    }));

  const validateStep = () => {
    setError("");
    if (step === 0) {
      const a = parseInt(form.age, 10);
      if (!form.full_name.trim() || form.full_name.trim().length < 2)
        return "Please enter your full name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        return "Please enter a valid email.";
      if (!form.phone.trim()) return "Phone is required.";
      if (!form.city.trim()) return "City is required.";
      if (!form.state.trim()) return "State is required.";
      if (!a || a < 18 || a > 99) return "Age must be between 18 and 99.";
    }
    if (step === 1) {
      if (form.categories.length === 0)
        return "Pick at least one lane you belong to.";
      if (form.story.trim().length < 10)
        return "Tell us a bit more (min 10 characters).";
      if (form.story.length > 600)
        return "Story is too long (max 600 characters).";
      const y = parseInt(form.years_in_culture, 10);
      if (isNaN(y) || y < 0 || y > 80)
        return "Please enter a valid number of years.";
    }
    if (step === 2) {
      if (!form.vehicle.trim() || form.vehicle.trim().length < 2)
        return "Please tell us what you drive, ride, or captain.";
      if (isBNB && !form.bnb_exchange_custom_work)
        return "Please answer the Built Not Bought question about exchanging custom work.";
    }
    if (step === 3) {
      if (form.photos.length < MIN_PHOTOS)
        return `Please upload at least ${MIN_PHOTOS} photos.`;
      if (form.photos.length > MAX_PHOTOS)
        return `Max ${MAX_PHOTOS} photos allowed.`;
    }
    if (step === 4) {
      if (!form.video_file_id && !form.video_url.trim())
        return "Please upload a video OR paste a link.";
      if (!form.video_file_id) {
        const u = form.video_url.trim();
        if (!/^https?:\/\/\S+/i.test(u))
          return "Please paste a valid link starting with http:// or https://";
      }
    }
    return "";
  };

  const next = () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const prev = () => {
    setError("");
    if (step > 0) setStep(step - 1);
  };

  const handlePhotosChange = async (files) => {
    setError("");
    const arr = Array.from(files);
    const slots = MAX_PHOTOS - form.photos.length;
    if (slots <= 0) {
      setError(`Max ${MAX_PHOTOS} photos.`);
      return;
    }
    const chosen = arr.slice(0, slots);
    try {
      const processed = [];
      for (const f of chosen) {
        if (!f.type.startsWith("image/")) continue;
        const data = await resizeImageToDataURL(f);
        processed.push({ data, name: f.name });
      }
      update({ photos: [...form.photos, ...processed] });
    } catch (e) {
      setError("Could not read one of the photos. Try a different file.");
    }
  };

  const removePhoto = (idx) => {
    update({ photos: form.photos.filter((_, i) => i !== idx) });
  };

  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  const handleVideoUpload = async (file) => {
    setError("");
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setError("Please choose a video file.");
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      setError("Video is over 50 MB. Compress it or paste a link instead.");
      return;
    }
    setVideoUploading(true);
    setVideoProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/applications/video`, fd, {
        onUploadProgress: (p) => {
          if (p.total) setVideoProgress(Math.round((p.loaded / p.total) * 100));
        },
      });
      update({
        video_file_id: res.data.file_id,
        video_filename: res.data.filename,
        video_url: "",
      });
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "Video upload failed. Try again.";
      setError(typeof msg === "string" ? msg : "Video upload failed.");
    } finally {
      setVideoUploading(false);
    }
  };

  const submit = async () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        age: parseInt(form.age, 10),
        categories: form.categories,
        story: form.story.trim(),
        years_in_culture: parseInt(form.years_in_culture, 10),
        vehicle: form.vehicle.trim(),
        social_handle: form.social_handle.trim(),
        photos: form.photos,
        video_file_id: form.video_file_id || null,
        video_url: form.video_url.trim() || null,
        show_context: showContext?.kind || "",
        extra_answers: isBNB
          ? { exchange_custom_work: form.bnb_exchange_custom_work }
          : {},
      };
      const res = await axios.post(`${API}/applications`, payload);
      setSuccess(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(
        typeof d === "string"
          ? d
          : Array.isArray(d)
            ? d.map((x) => x.msg).join(", ")
            : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = useMemo(
    () => Math.round(((step + 1) / STEPS.length) * 100),
    [step],
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-6"
          data-testid="apply-modal"
          onClick={onClose}
        >
        <motion.div
          key="dialog"
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl bg-[#0d0d14] border border-white/10 min-h-screen sm:min-h-0 sm:rounded-none shadow-[0_30px_100px_rgba(0,229,255,0.12)]"
        >
          {/* Close */}
          <button
            onClick={onClose}
            data-testid="apply-modal-close"
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-[#FF2D9C] hover:text-white text-white/70 border border-white/10 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header + progress */}
          <div className="px-6 sm:px-10 pt-8 pb-5 border-b border-white/10">
            <div className="font-accent text-xs text-[#FF2D9C] mb-2 tracking-[0.22em]">
              AUTOMOTOVILLE · {showContext?.title ? showContext.title.toUpperCase() : "CASTING APPLICATION"}
            </div>
            <div className="flex items-baseline justify-between">
              <h2
                className="font-heading text-2xl sm:text-3xl font-black"
                data-testid="apply-step-title"
              >
                {success ? "You're in." : STEPS[step].title}
              </h2>
              {!success && (
                <span className="font-accent text-xs text-white/50">
                  STEP {step + 1} / {STEPS.length}
                </span>
              )}
            </div>
            {!success && (
              <div className="mt-5 h-[2px] bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#FF2D9C] to-[#00E5FF]"
                  initial={false}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            )}
          </div>

          {/* Body */}
          <div className="px-6 sm:px-10 py-8 min-h-[320px]">
            {success ? (
              <SuccessScreen data={success} onClose={onClose} />
            ) : (
              <>
                {step === 0 && (
                  <StepAbout form={form} update={update} />
                )}
                {step === 1 && (
                  <StepLane
                    form={form}
                    update={update}
                    toggleCategory={toggleCategory}
                  />
                )}
                {step === 2 && <StepGear form={form} update={update} isBNB={isBNB} />}
                {step === 3 && (
                  <StepPhotos
                    form={form}
                    handlePhotosChange={handlePhotosChange}
                    removePhoto={removePhoto}
                  />
                )}
                {step === 4 && (
                  <StepVideo
                    form={form}
                    update={update}
                    handleVideoUpload={handleVideoUpload}
                    videoUploading={videoUploading}
                    videoProgress={videoProgress}
                  />
                )}
                {step === 5 && <StepReview form={form} />}

                {error && (
                  <div
                    data-testid="apply-error"
                    className="mt-5 flex items-start gap-2 text-sm text-[#FF2D9C] border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 px-4 py-3"
                  >
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer actions */}
          {!success && (
            <div className="px-6 sm:px-10 py-5 border-t border-white/10 flex items-center justify-between gap-3 bg-black/40 sticky bottom-0">
              <button
                onClick={prev}
                disabled={step === 0}
                data-testid="apply-prev-btn"
                className="flex items-center gap-2 px-4 py-3 font-accent text-xs tracking-widest text-white/70 disabled:opacity-30 disabled:cursor-not-allowed hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              {step < STEPS.length - 1 ? (
                <button
                  onClick={next}
                  data-testid="apply-next-btn"
                  className="flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-8 py-3 uppercase tracking-[0.22em] text-sm glow-pink transition-all hover:scale-[1.02]"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="apply-submit-btn"
                  className="flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-8 py-3 uppercase tracking-[0.22em] text-sm glow-pink transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-wait"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> Submit Application
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------- step components ----------
const Label = ({ children }) => (
  <label className="block font-accent text-[11px] tracking-[0.22em] text-white/60 mb-2">
    {children}
  </label>
);

const inputCls =
  "w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 px-4 py-3 text-white placeholder-white/30 outline-none transition-colors";

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function StepAbout({ form, update }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-white/60">
        The basics. This is how we reach out when producers love your tape.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="FULL NAME *" testid="field-full-name">
          <input
            className={inputCls}
            placeholder="Jane Driver"
            value={form.full_name}
            onChange={(e) => update({ full_name: e.target.value })}
            data-testid="input-full-name"
          />
        </Field>
        <Field label="EMAIL *" testid="field-email">
          <input
            className={inputCls}
            type="email"
            placeholder="you@domain.com"
            value={form.email}
            onChange={(e) => update({ email: e.target.value })}
            data-testid="input-email"
          />
        </Field>
        <Field label="PHONE *" testid="field-phone">
          <input
            className={inputCls}
            placeholder="(555) 555-0100"
            value={form.phone}
            onChange={(e) => update({ phone: e.target.value })}
            data-testid="input-phone"
          />
        </Field>
        <Field label="AGE *" testid="field-age">
          <input
            className={inputCls}
            type="number"
            min="18"
            max="99"
            placeholder="32"
            value={form.age}
            onChange={(e) => update({ age: e.target.value })}
            data-testid="input-age"
          />
        </Field>
        <Field label="CITY *" testid="field-city">
          <input
            className={inputCls}
            placeholder="Atlanta"
            value={form.city}
            onChange={(e) => update({ city: e.target.value })}
            data-testid="input-city"
          />
        </Field>
        <Field label="STATE *" testid="field-state">
          <input
            className={inputCls}
            placeholder="GA"
            value={form.state}
            onChange={(e) => update({ state: e.target.value })}
            data-testid="input-state"
          />
        </Field>
      </div>
    </div>
  );
}

function StepLane({ form, update, toggleCategory }) {
  return (
    <div className="space-y-6">
      <div>
        <Label>YOUR LANE * (choose one or more)</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CATS.map((c) => {
            const active = form.categories.includes(c.key);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => toggleCategory(c.key)}
                data-testid={`cat-${c.key}-btn`}
                className={`flex flex-col items-center gap-2 py-5 border transition-all ${
                  active
                    ? "border-[#FF2D9C] bg-[#FF2D9C]/10 text-white shadow-[0_0_25px_rgba(255,45,156,0.35)]"
                    : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/30"
                }`}
              >
                <c.Icon
                  className={`w-6 h-6 ${active ? "text-[#FF2D9C]" : "text-[#00E5FF]"}`}
                />
                <span className="font-accent text-xs tracking-widest">
                  {c.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Field
        label={`YOUR STORY * (${form.story.length}/600)`}
        testid="field-story"
      >
        <textarea
          className={inputCls + " resize-none min-h-[130px]"}
          rows={5}
          maxLength={600}
          placeholder="Why should we film you? What makes your corner of the culture different?"
          value={form.story}
          onChange={(e) => update({ story: e.target.value })}
          data-testid="input-story"
        />
      </Field>

      <Field label="YEARS IN THE CULTURE *" testid="field-years">
        <input
          className={inputCls + " max-w-[180px]"}
          type="number"
          min="0"
          max="80"
          placeholder="10"
          value={form.years_in_culture}
          onChange={(e) => update({ years_in_culture: e.target.value })}
          data-testid="input-years"
        />
      </Field>
    </div>
  );
}

function StepGear({ form, update, isBNB }) {
  const bnbOptions = ["Yes", "Discuss", "No"];
  return (
    <div className="space-y-5">
      <p className="text-sm text-white/60">
        Tell us about the steel, chrome, or hull that tells your story.
      </p>
      <Field label="VEHICLE / RIG / BOAT *" testid="field-vehicle">
        <input
          className={inputCls}
          placeholder="e.g., 2019 Kenworth W900, 1970 Chevelle SS, 34ft Fountain"
          value={form.vehicle}
          onChange={(e) => update({ vehicle: e.target.value })}
          data-testid="input-vehicle"
        />
      </Field>
      <Field label="SOCIAL HANDLE (OPTIONAL)" testid="field-social">
        <input
          className={inputCls}
          placeholder="@yourhandle (Instagram / TikTok / YouTube)"
          value={form.social_handle}
          onChange={(e) => update({ social_handle: e.target.value })}
          data-testid="input-social"
        />
      </Field>

      {isBNB && (
        <div
          data-testid="field-bnb-exchange"
          className="mt-6 pt-5 border-t border-[#FF2D9C]/30"
        >
          <div className="font-accent text-[11px] tracking-[0.22em] text-[#FF2D9C] mb-2">
            BUILT NOT BOUGHT — EXTRA QUESTION *
          </div>
          <p className="text-sm text-white/80 mb-3 leading-relaxed">
            Are you open to exchanging custom work for visibility, features, or
            network access on the show?
          </p>
          <div className="grid grid-cols-3 gap-2">
            {bnbOptions.map((opt) => {
              const active = form.bnb_exchange_custom_work === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => update({ bnb_exchange_custom_work: opt })}
                  data-testid={`bnb-exchange-${opt.toLowerCase()}`}
                  className={`px-4 py-3 border text-sm transition-all ${
                    active
                      ? "border-[#FF2D9C] bg-[#FF2D9C]/10 text-white"
                      : "border-white/10 bg-white/[0.02] text-white/75 hover:border-white/30"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StepPhotos({ form, handlePhotosChange, removePhoto }) {
  const inputRef = useRef(null);
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading text-xl font-black mb-1">
          Upload Photos{" "}
          <span className="text-[#FF2D9C]">(No Filters — We Want the Real You)</span>
        </h3>
        <p className="text-sm text-white/65 leading-relaxed">
          2–5 clear, high-quality photos. No heavy filters. A mix of portrait
          and lifestyle shots (you with your rig, on the road, in the shop) is
          ideal. Good lighting beats a fancy camera every time — phone photos
          are perfectly fine.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {form.photos.map((p, i) => (
          <div
            key={i}
            data-testid={`photo-preview-${i}`}
            className="relative aspect-square border border-white/10 overflow-hidden group"
          >
            <img
              src={p.data}
              alt={p.name || `photo-${i}`}
              className="w-full h-full object-cover"
            />
            <button
              onClick={() => removePhoto(i)}
              data-testid={`photo-remove-${i}`}
              className="absolute top-2 right-2 w-8 h-8 bg-black/80 hover:bg-[#FF2D9C] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="remove"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <div className="absolute bottom-0 inset-x-0 px-2 py-1 text-[10px] font-accent text-white/80 bg-gradient-to-t from-black to-transparent truncate">
              {i + 1} / {MAX_PHOTOS}
            </div>
          </div>
        ))}

        {form.photos.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="photo-add-btn"
            className="aspect-square border-2 border-dashed border-white/20 hover:border-[#00E5FF]/60 hover:bg-[#00E5FF]/5 flex flex-col items-center justify-center gap-2 text-white/60 hover:text-[#00E5FF] transition-all"
          >
            <Upload className="w-6 h-6" />
            <span className="font-accent text-[11px] tracking-widest">
              ADD PHOTO
            </span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handlePhotosChange(e.target.files)}
        data-testid="photo-file-input"
      />

      <div className="text-xs text-white/45 font-accent">
        {form.photos.length} / {MAX_PHOTOS} UPLOADED · MIN {MIN_PHOTOS}
      </div>
    </div>
  );
}

function StepVideo({
  form,
  update,
  handleVideoUpload,
  videoUploading,
  videoProgress,
}) {
  const inputRef = useRef(null);
  const hasFile = !!form.video_file_id;
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-heading text-xl font-black mb-1">
          Intro Video{" "}
          <span className="text-[#00E5FF]">(Be Real. Be You.)</span>
        </h3>
        <p className="text-sm text-white/65 leading-relaxed">
          30–60 seconds. Shot on phone is fine. Clear audio is a must. No heavy
          filters or edits. Natural personality encouraged.
        </p>
        <p className="text-sm text-white/55 mt-3 italic border-l-2 border-[#FF2D9C]/60 pl-3">
          &ldquo;Talk like you&rsquo;re introducing yourself on a reality show.
          Be confident, be real, and don&rsquo;t overthink it.&rdquo;
        </p>
        <ul className="mt-3 space-y-1 text-sm text-white/70">
          <li>· Who are you?</li>
          <li>· What do you do?</li>
          <li>· Why should you be on AutoMotoVille?</li>
        </ul>
      </div>

      {/* Upload card */}
      <div className="border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Video className="w-4 h-4 text-[#00E5FF]" />
          <span className="font-accent text-xs tracking-widest text-white/70">
            UPLOAD A FILE (MAX 50 MB)
          </span>
        </div>
        {hasFile ? (
          <div
            data-testid="video-uploaded"
            className="flex items-center justify-between gap-3 bg-[#00E5FF]/10 border border-[#00E5FF]/40 p-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <CheckCircle2 className="w-5 h-5 text-[#00E5FF] shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-white truncate">
                  {form.video_filename || "Video uploaded"}
                </div>
                <div className="text-[11px] font-accent text-white/50">
                  READY TO SUBMIT
                </div>
              </div>
            </div>
            <button
              onClick={() =>
                update({ video_file_id: null, video_filename: "" })
              }
              data-testid="video-remove-btn"
              className="text-white/60 hover:text-[#FF2D9C] p-2"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={videoUploading}
            data-testid="video-choose-btn"
            className="w-full border-2 border-dashed border-white/20 hover:border-[#00E5FF]/60 py-8 flex flex-col items-center gap-2 text-white/70 hover:text-[#00E5FF] transition-all disabled:opacity-60"
          >
            {videoUploading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="font-accent text-[11px] tracking-widest">
                  UPLOADING… {videoProgress}%
                </span>
                <div className="w-56 h-[3px] bg-white/10 mt-2">
                  <div
                    className="h-full bg-gradient-to-r from-[#FF2D9C] to-[#00E5FF]"
                    style={{ width: `${videoProgress}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6" />
                <span className="font-accent text-[11px] tracking-widest">
                  CHOOSE VIDEO FILE
                </span>
                <span className="text-[11px] text-white/40">
                  MP4, MOV, WebM · up to 50 MB
                </span>
              </>
            )}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => handleVideoUpload(e.target.files?.[0])}
          data-testid="video-file-input"
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-white/10" />
        <span className="font-accent text-[11px] text-white/40 tracking-widest">
          OR
        </span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Paste link alternative */}
      <div className="border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-3">
          <LinkIcon className="w-4 h-4 text-[#FF2D9C]" />
          <span className="font-accent text-xs tracking-widest text-white/70">
            PASTE A LINK (YOUTUBE · GOOGLE DRIVE · DROPBOX)
          </span>
        </div>
        <input
          className={inputCls}
          type="url"
          placeholder="https://youtu.be/... or drive.google.com/..."
          value={form.video_url}
          onChange={(e) =>
            update({
              video_url: e.target.value,
              ...(e.target.value
                ? { video_file_id: null, video_filename: "" }
                : {}),
            })
          }
          data-testid="input-video-url"
          disabled={hasFile}
        />
        {hasFile && (
          <p className="text-[11px] text-white/40 mt-2">
            Remove the uploaded file to paste a link instead.
          </p>
        )}
      </div>
    </div>
  );
}

function StepReview({ form }) {
  const Row = ({ label, value }) => (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-white/5">
      <span className="font-accent text-[11px] tracking-widest text-white/50">
        {label}
      </span>
      <span className="text-sm text-white text-right break-words max-w-[65%]">
        {value || <span className="text-white/30">—</span>}
      </span>
    </div>
  );
  return (
    <div className="space-y-5">
      <p className="text-sm text-white/60">
        Last look. If everything is 100% you, hit submit.
      </p>
      <div className="border border-white/10 bg-black/30 px-5 py-3">
        <Row
          label="NAME"
          value={`${form.full_name} · age ${form.age}`}
        />
        <Row label="EMAIL" value={form.email} />
        <Row label="PHONE" value={form.phone} />
        <Row label="LOCATION" value={`${form.city}, ${form.state}`} />
        <Row label="LANES" value={form.categories.join(" · ").toUpperCase()} />
        <Row label="YEARS IN CULTURE" value={form.years_in_culture} />
        <Row label="VEHICLE / RIG" value={form.vehicle} />
        <Row label="SOCIAL" value={form.social_handle} />
        <Row label="PHOTOS" value={`${form.photos.length} uploaded`} />
        <Row
          label="VIDEO"
          value={
            form.video_file_id
              ? `File: ${form.video_filename}`
              : form.video_url
                ? `Link: ${form.video_url}`
                : ""
          }
        />
      </div>
      <div className="text-sm text-white/60 leading-relaxed bg-[#00E5FF]/5 border border-[#00E5FF]/20 p-4">
        <p className="font-accent text-[11px] tracking-widest text-[#00E5FF] mb-1">
          STORY
        </p>
        <p>&ldquo;{form.story}&rdquo;</p>
      </div>
    </div>
  );
}

function SuccessScreen({ data, onClose }) {
  return (
    <div data-testid="apply-success" className="text-center py-6 space-y-6">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FF2D9C]/10 border border-[#FF2D9C]/40 pulse-ring">
        <CheckCircle2 className="w-10 h-10 text-[#FF2D9C]" />
      </div>
      <div>
        <h3 className="font-heading text-3xl font-black">
          You&rsquo;re <span className="neon-text">in the pipeline.</span>
        </h3>
        <p className="text-white/70 mt-3 max-w-md mx-auto leading-relaxed">
          {data?.message ||
            "Thanks — we got it. You'll hear back within 14 days."}
        </p>
        <p className="text-white/40 font-accent text-[11px] tracking-widest mt-4">
          APPLICATION ID · {data?.id?.slice(0, 8).toUpperCase()}
        </p>
      </div>
      <button
        onClick={onClose}
        data-testid="apply-success-close-btn"
        className="bg-[#FF2D9C] text-white font-black px-8 py-3 uppercase tracking-[0.22em] text-sm glow-pink transition-all hover:scale-[1.02]"
      >
        Close
      </button>
    </div>
  );
}
